import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChartLineUp, CurrencyDollar, Users, Rocket, ArrowClockwise,
  Warning, TrendUp, Clock, CheckCircle, Sparkle, ArrowUpRight, Heart, Eye, CursorClick, ArrowDown
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MercuryReaperCard from "../components/MercuryReaperCard";
import RateCardShareCard from "../components/RateCardShareCard";
import AdFunnelCard from "../components/admin/AdFunnelCard";
import CheckoutNudgeCard from "../components/admin/CheckoutNudgeCard";
import PixelHealthCard from "../components/admin/PixelHealthCard";
import OwnerAlertsCard from "../components/admin/OwnerAlertsCard";
import WeeklyReportCard from "../components/admin/WeeklyReportCard";
import ExperimentsCard from "../components/admin/ExperimentsCard";
import FollowUpCard from "../components/admin/FollowUpCard";

const PLAN_META = {
  solo_monthly: { name: "Solo", color: "#FF5A1F" },
  duo_monthly: { name: "Duo", color: "#FFA24C" },
  trio_monthly: { name: "Trio", color: "#FFD24C" },
  small_fleet_monthly: { name: "Small Fleet", color: "#007AFF" },
  growth_monthly: { name: "Growth", color: "#00D4AA" },
};

function fmtUSD(n) {
  return `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function StatCard({ icon: Icon, label, value, hint, tone = "default", testid }) {
  const toneMap = {
    default: "text-white",
    money: "text-[#FF5A1F]",
    good: "text-emerald-400",
    warn: "text-amber-400",
    info: "text-[#007AFF]",
  };
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
        <Icon size={13} />
        {label}
      </div>
      <div className={`text-3xl font-display font-semibold tracking-tight ${toneMap[tone]}`}>{value}</div>
      {hint && <div className="text-xs text-neutral-500 mt-2">{hint}</div>}
    </div>
  );
}

function Sparkline({ series }) {
  const maxY = Math.max(1, ...series.map((s) => s.count));
  const w = 100;
  const h = 30;
  const step = series.length > 1 ? w / (series.length - 1) : 0;
  const points = series.map((s, i) => {
    const x = i * step;
    const y = h - (s.count / maxY) * h;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const areaPoints = `0,${h} ${points} ${w},${h}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-16" data-testid="signups-sparkline">
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF5A1F" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FF5A1F" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill="url(#sparkGrad)" />
      <polyline points={points} fill="none" stroke="#FF5A1F" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function PlanDistBar({ plan_breakdown, total }) {
  const entries = Object.entries(plan_breakdown || {});
  if (entries.length === 0) {
    return (
      <div className="text-neutral-500 text-sm py-6 text-center border border-dashed border-white/10 rounded-lg">
        No paid subscribers yet. First conversion incoming — stay tuned. 🚀
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="plan-distribution">
      {entries.map(([key, count]) => {
        const meta = PLAN_META[key] || { name: key, color: "#888" };
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={key} data-testid={`plan-row-${key}`}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-white font-medium">{meta.name}</span>
              <span className="text-neutral-400">{count} {count === 1 ? "sub" : "subs"} · {pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UserRow({ u, testid, onFlagChange, bulkMode = false, isSelected = false, onToggleSelect }) {
  const planName = u.plan ? (PLAN_META[u.plan]?.name || u.plan) : null;
  const [busy, setBusy] = useState(false);

  const toggleFlag = async () => {
    const next = !u.is_test_user;
    setBusy(true);
    try {
      await client.post(`/admin/metrics/users/${u.id}/mark-test-user`, {
        is_test_user: next,
      });
      toast.success(next ? "Marked as test user" : "Unflagged as test user");
      onFlagChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Flag update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex items-center justify-between gap-3 py-2.5 border-b border-white/[0.05] last:border-0 ${
      isSelected ? "bg-blue-500/[0.05]" : ""
    }`} data-testid={testid}>
      {bulkMode && (
        <button
          onClick={() => onToggleSelect?.(u.id)}
          data-testid={`${testid}-checkbox`}
          className={`shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected
              ? "bg-blue-500 border-blue-500"
              : "border-neutral-500 hover:border-blue-400"
          }`}
          aria-checked={isSelected}
          role="checkbox"
        >
          {isSelected && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1 5l3 3L9 2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate flex items-center gap-2">
          {u.full_name || u.email}
          {u.is_test_user && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-neutral-700/60 text-neutral-400 border border-white/10 font-semibold uppercase tracking-widest" data-testid={`${testid}-test-user-badge`}>
              TEST
            </span>
          )}
        </div>
        <div className="text-[11px] text-neutral-500 truncate">
          {u.email}
          {u.company_name ? ` · ${u.company_name}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {u.is_pilot && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF5A1F]/15 text-[#FF5A1F] border border-[#FF5A1F]/30 font-semibold">
            PILOT
          </span>
        )}
        {planName && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-semibold">
            {planName}
          </span>
        )}
        <div className="text-[11px] text-neutral-500 whitespace-nowrap">{fmtRelative(u.created_at)}</div>
        <button
          onClick={toggleFlag}
          disabled={busy}
          data-testid={`${testid}-mark-test-btn`}
          title={u.is_test_user ? "Un-flag as test user" : "Mark as test user (hidden by Hide Test Users toggle)"}
          className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold transition-colors disabled:opacity-50 ${
            u.is_test_user
              ? "bg-yellow-500/15 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/25"
              : "bg-white/[0.02] border-white/10 text-neutral-500 hover:border-white/25 hover:text-neutral-300"
          }`}
        >
          {busy ? "…" : u.is_test_user ? "Un-flag" : "Mark test"}
        </button>
      </div>
    </div>
  );
}

const NURTURE_STEPS = [
  { key: "day1",  label: "Day 1",  full: "Welcome",        color: "text-emerald-400" },
  { key: "day3",  label: "Day 3",  full: "AI Negotiator",  color: "text-orange-400"  },
  { key: "day7",  label: "Day 7",  full: "Mid-trial",       color: "text-blue-400"    },
  { key: "day12", label: "Day 12", full: "Trial ending",   color: "text-red-400"     },
];

function ConversionCard({ data }) {
  if (!data) return null;
  const trend = (data.signups_this_week || 0) - (data.signups_last_week || 0);
  const trendPct = data.signups_last_week > 0
    ? Math.round((trend / data.signups_last_week) * 100)
    : null;
  return (
    <div className="card-surface p-5" data-testid="conversion-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500">
          <Heart size={13} weight="fill" className="text-[#FF5A1F]" /> Trial → Paid Funnel
        </div>
        <div className="text-[10px] text-neutral-600 uppercase tracking-widest">Last 30 days</div>
      </div>

      {/* Top-line funnel: 4 stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3" data-testid="funnel-signups-this-week">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Signups this wk</div>
          <div className="flex items-baseline gap-2">
            <div className="text-2xl font-display font-semibold text-white tabular-nums">
              {data.signups_this_week ?? 0}
            </div>
            {trendPct !== null && (
              <div className={`text-[11px] tabular-nums inline-flex items-center gap-0.5 ${
                trend >= 0 ? "text-emerald-400" : "text-red-400"
              }`}>
                {trend >= 0 ? <ArrowUpRight size={10} weight="bold" /> : <ArrowDown size={10} weight="bold" />}
                {Math.abs(trendPct)}%
              </div>
            )}
          </div>
          <div className="text-[10px] text-neutral-600 mt-1">vs {data.signups_last_week ?? 0} last wk</div>
        </div>

        <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3" data-testid="funnel-still-trialing">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Still trialing</div>
          <div className="text-2xl font-display font-semibold text-blue-400 tabular-nums">
            {data.still_trialing ?? 0}
          </div>
          <div className="text-[10px] text-neutral-600 mt-1">active in funnel</div>
        </div>

        <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3" data-testid="funnel-converted">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Paid this wk</div>
          <div className="text-2xl font-display font-semibold text-emerald-400 tabular-nums">
            {data.converted_to_paid_this_week ?? 0}
          </div>
          <div className="text-[10px] text-neutral-600 mt-1">
            {data.converted_to_paid_all_time ?? 0} all-time
          </div>
        </div>

        <div className="rounded-lg bg-white/[0.02] border border-white/5 p-3" data-testid="funnel-churned">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Trial expired</div>
          <div className="text-2xl font-display font-semibold text-red-400 tabular-nums">
            {data.churned_before_day_14 ?? 0}
          </div>
          <div className="text-[10px] text-neutral-600 mt-1">last 30d, never upgraded</div>
        </div>
      </div>

      {/* Conversion rate + median days */}
      <div className="flex items-center justify-between gap-3 mb-6 py-3 border-y border-white/[0.06]">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Trial→Paid rate</div>
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-display font-semibold text-[#FF5A1F] tabular-nums" data-testid="conversion-rate">
              {data.conversion_rate_30d_pct ?? 0}%
            </div>
            <div className="text-[11px] text-neutral-500">
              of last-30d cohort
            </div>
          </div>
        </div>
        {data.median_days_to_convert !== null && data.median_days_to_convert !== undefined && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Median days to convert</div>
            <div className="text-2xl font-display font-semibold text-white tabular-nums" data-testid="median-days-to-convert">
              {data.median_days_to_convert}d
            </div>
          </div>
        )}
      </div>

      {/* Per-step nurture performance */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
          Nurture email performance by step
        </div>
        <div className="space-y-2" data-testid="nurture-perf-table">
          {NURTURE_STEPS.map((s) => {
            const stats = data.nurture_per_step?.[s.key] || {};
            const sent = stats.sent || 0;
            const opens = stats.opened || 0;
            const clicks = stats.clicked || 0;
            const openRate = stats.open_rate_pct || 0;
            const clickRate = stats.click_rate_pct || 0;
            // Bar width scaled by open rate (0-100)
            const openBarWidth = Math.min(100, openRate);
            return (
              <div key={s.key}
                   className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 flex items-center gap-3 flex-wrap"
                   data-testid={`nurture-perf-${s.key}`}>
                <div className={`text-[10px] uppercase tracking-widest ${s.color} font-semibold shrink-0 w-14`}>
                  {s.label}
                </div>
                <div className="text-[11px] text-neutral-500 shrink-0 w-24">{s.full}</div>
                <div className="flex-1 min-w-[140px]">
                  {/* Open rate progress bar */}
                  <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-500/70 to-emerald-400"
                      style={{ width: `${openBarWidth}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-[11px] tabular-nums text-neutral-400">
                  <span title="Sent">
                    <span className="text-neutral-500">Sent</span>{" "}
                    <b className="text-neutral-200">{sent}</b>
                  </span>
                  <span title={`${openRate}% open rate`} className="inline-flex items-center gap-1">
                    <Eye size={11} weight="fill" className="text-emerald-400" />
                    <b className="text-neutral-200">{opens}</b>
                    <span className="text-neutral-500">({openRate}%)</span>
                  </span>
                  <span title={`${clickRate}% click rate`} className="inline-flex items-center gap-1">
                    <CursorClick size={11} weight="fill" className="text-orange-400" />
                    <b className="text-neutral-200">{clicks}</b>
                    <span className="text-neutral-500">({clickRate}%)</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function AdminMetricsPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [signups, setSignups] = useState(null);
  const [activity, setActivity] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  // Hide Test Users toggle — Wave 7. Filters out pytest / demo / seed
  // accounts (heuristic in backend `is_test_email()`). Persist via localStorage.
  const [hideTestUsers, setHideTestUsers] = useState(() => {
    try {
      return localStorage.getItem("adminMetricsHideTestUsers") === "1";
    } catch (e) {
      return false;
    }
  });
  // Wave 9: Bulk-select mode + selected user IDs
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const reload = async (days = range, hide = hideTestUsers) => {
    setLoading(true);
    try {
      const suffix = hide ? "&hide_test_users=true" : "";
      const [ov, sg, ac, cv] = await Promise.all([
        client.get(`/admin/metrics/overview?${suffix.slice(1)}`),
        client.get(`/admin/metrics/signups?days=${days}${suffix}`),
        client.get(`/admin/metrics/activity?${suffix.slice(1)}`),
        client.get("/admin/metrics/conversion"),
      ]);
      setOverview(ov.data);
      setSignups(sg.data);
      setActivity(ac.data);
      setConversion(cv.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load metrics");
    } finally {
      setLoading(false);
    }
  };

  const toggleHideTestUsers = () => {
    const next = !hideTestUsers;
    setHideTestUsers(next);
    try {
      localStorage.setItem("adminMetricsHideTestUsers", next ? "1" : "0");
    } catch (e) {
      /* silent — private mode etc */
    }
    reload(range, next);
  };

  const toggleBulkMode = () => {
    setBulkSelectMode((prev) => !prev);
    setSelectedUserIds(new Set());
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const bulkFlagAsTest = async (mark = true) => {
    if (selectedUserIds.size === 0) {
      toast.error("Select at least one user first");
      return;
    }
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedUserIds);
      const { data } = await client.post("/admin/metrics/users/bulk-mark-test-user", {
        user_ids: ids,
        is_test_user: mark,
      });
      toast.success(`${mark ? "Flagged" : "Un-flagged"} ${data.modified} user${data.modified === 1 ? "" : "s"} as test`);
      setSelectedUserIds(new Set());
      reload(range, hideTestUsers);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const [autoflagBusy, setAutoflagBusy] = useState(false);
  const [autoflagLastRun, setAutoflagLastRun] = useState(null);
  const [undoBusy, setUndoBusy] = useState(false);

  const loadAutoflagStatus = async () => {
    try {
      const { data } = await client.get("/admin/metrics/test-user-autoflag/status");
      // Find newest run with actual flags AND no undone_at
      const undoable = (data.runs || []).find(
        (r) => (r.newly_flagged || 0) > 0 && !r.undone_at
      );
      setAutoflagLastRun(undoable || null);
    } catch (e) {
      // silent — non-blocking widget
    }
  };

  const runAutoflag = async () => {
    if (!window.confirm("Auto-detect and flag dormant test accounts?\n\nThis scans every user, matches the heuristic (test emails, .example domains, pytest prefixes), and flags any with zero activity in the last 30 days. Admins are always skipped.")) return;
    setAutoflagBusy(true);
    try {
      const { data } = await client.post("/admin/metrics/test-user-autoflag/run-now");
      toast.success(`Auto-flag: scanned ${data.scanned}, flagged ${data.newly_flagged} new dormant test users`);
      reload(range, hideTestUsers);
      loadAutoflagStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-flag failed");
    } finally {
      setAutoflagBusy(false);
    }
  };

  const undoAutoflag = async () => {
    if (!autoflagLastRun) return;
    const n = autoflagLastRun.newly_flagged || 0;
    if (!window.confirm(`Undo the last auto-detect run?\n\nThis will un-flag every ${n} user${n === 1 ? "" : "s"} the last scan touched. Manually flagged users are never touched.`)) return;
    setUndoBusy(true);
    try {
      const { data } = await client.post("/admin/metrics/test-user-autoflag/undo");
      if (data.undone) {
        toast.success(`Undone: un-flagged ${data.modified} user${data.modified === 1 ? "" : "s"} from ${data.run_day}`);
        reload(range, hideTestUsers);
        loadAutoflagStatus();
      } else {
        toast.error(data.reason === "no_recent_run"
          ? "No recent auto-detect run to undo"
          : `Cannot undo: ${data.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setUndoBusy(false);
    }
  };

  useEffect(() => {
    reload(range, hideTestUsers);
    loadAutoflagStatus();
    /* eslint-disable-next-line */
  }, []);

  const peakDay = useMemo(() => {
    if (!signups?.series?.length) return null;
    return signups.series.reduce((max, s) => (s.count > max.count ? s : max), signups.series[0]);
  }, [signups]);

  if (!user?.is_admin) {
    return (
      <div className="card-surface p-8 text-center" data-testid="admin-metrics-forbidden">
        <Warning size={40} weight="fill" className="text-red-400 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-white mb-2">Admin only</h1>
        <p className="text-neutral-400 text-sm">This dashboard is restricted to admin accounts.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" data-testid="admin-metrics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
            <Sparkle size={12} weight="fill" /> FOUNDER DASHBOARD
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Metrics
          </h1>
          <p className="text-neutral-400 mt-2 text-sm sm:text-base">
            Live business snapshot — MRR, active pilots, trial pipeline, and conversion.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={runAutoflag}
            disabled={autoflagBusy}
            data-testid="metrics-autoflag-btn"
            title="Nightly auto-detect: scan for dormant test accounts and flag them"
            className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-colors disabled:opacity-50 bg-white/[0.03] border-white/10 text-neutral-400 hover:border-emerald-500/40 hover:text-emerald-300"
          >
            {autoflagBusy ? <ArrowClockwise size={11} className="animate-spin" /> : <span>🔍</span>}
            Auto-detect
          </button>
          {/* Wave 11: Undo last autoflag run */}
          {autoflagLastRun && (
            <button
              onClick={undoAutoflag}
              disabled={undoBusy}
              data-testid="metrics-autoflag-undo-btn"
              title={`Un-flag every user flagged in the last run (${autoflagLastRun.day}, ${autoflagLastRun.newly_flagged} users)`}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-colors disabled:opacity-50 bg-white/[0.03] border-white/10 text-neutral-400 hover:border-amber-500/40 hover:text-amber-300"
            >
              {undoBusy ? <ArrowClockwise size={11} className="animate-spin" /> : <span>↩</span>}
              Undo ({autoflagLastRun.newly_flagged})
            </button>
          )}
          <button
            onClick={toggleBulkMode}
            disabled={loading}
            data-testid="metrics-bulk-mode-toggle"
            title="Enable multi-select to bulk-flag users as test"
            className={`text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-colors disabled:opacity-50 ${
              bulkSelectMode
                ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                : "bg-white/[0.03] border-white/10 text-neutral-400 hover:border-white/25"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${bulkSelectMode ? "bg-blue-400" : "bg-neutral-500"}`} />
            {bulkSelectMode ? `Select mode (${selectedUserIds.size})` : "Multi-select"}
          </button>
          <button
            onClick={toggleHideTestUsers}
            disabled={loading}
            data-testid="metrics-hide-test-users-toggle"
            title="Hide pytest / demo / seed accounts from all counts"
            className={`text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-colors disabled:opacity-50 ${
              hideTestUsers
                ? "bg-[#FF5A1F]/15 border-[#FF5A1F]/40 text-[#FF9764]"
                : "bg-white/[0.03] border-white/10 text-neutral-400 hover:border-white/25"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${hideTestUsers ? "bg-[#FF5A1F]" : "bg-neutral-500"}`} />
            Hide test users
            {hideTestUsers && overview?.hidden_test_user_count > 0 && (
              <span className="text-[10px] opacity-70" data-testid="metrics-hidden-count">
                −{overview.hidden_test_user_count}
              </span>
            )}
          </button>
          <button
            onClick={() => reload(range, hideTestUsers)}
            disabled={loading}
            data-testid="metrics-refresh-btn"
            className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 hover:border-white/25 transition-colors disabled:opacity-50"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {loading && !overview ? (
        <div className="text-neutral-500 text-sm py-16 text-center">Loading metrics…</div>
      ) : (
        <>
          {/* Top-line stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={CurrencyDollar}
              label="Monthly Recurring Revenue"
              value={fmtUSD(overview?.mrr)}
              hint={overview?.arr ? `ARR: ${fmtUSD(overview.arr)}` : "No paid subs yet"}
              tone="money"
              testid="stat-mrr"
            />
            <StatCard
              icon={CheckCircle}
              label="Active Paid Subs"
              value={overview?.active_paid ?? 0}
              hint={
                (overview?.past_due || 0) > 0
                  ? `${overview.past_due} past due · ${overview.canceled} canceled`
                  : `${overview?.canceled ?? 0} canceled all-time`
              }
              tone="good"
              testid="stat-active-paid"
            />
            <StatCard
              icon={Rocket}
              label="Active Pilots"
              value={overview?.pilots_active ?? 0}
              hint={`${overview?.trialing_active ?? 0} trials active · ${overview?.trialing_expired ?? 0} expired`}
              tone="info"
              testid="stat-pilots-active"
            />
            <StatCard
              icon={TrendUp}
              label="Trial → Paid Conversion"
              value={`${overview?.conversion_rate_pct ?? 0}%`}
              hint={`Of ${(overview?.active_paid || 0) + (overview?.trialing_expired || 0)} decided trials`}
              tone={((overview?.conversion_rate_pct || 0) >= 15) ? "good" : "warn"}
              testid="stat-conversion"
            />
          </div>

          {/* Signups + Plan distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Signups sparkline (spans 3) */}
            <div className="card-surface p-5 lg:col-span-3" data-testid="signups-card">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500">
                  <ChartLineUp size={13} /> Signups (last {range} days)
                </div>
                <div className="flex gap-1">
                  {[7, 30, 90].map((d) => (
                    <button
                      key={d}
                      onClick={() => { setRange(d); reload(d, hideTestUsers); }}
                      data-testid={`signups-range-${d}`}
                      className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                        range === d
                          ? "border-[#FF5A1F]/50 bg-[#FF5A1F]/15 text-[#FF5A1F]"
                          : "border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-baseline gap-3 mb-3">
                <div className="text-4xl font-display font-semibold text-white">{signups?.total ?? 0}</div>
                <div className="text-xs text-neutral-500">total new signups</div>
              </div>
              {signups?.series && <Sparkline series={signups.series} />}
              <div className="flex items-center justify-between text-[10px] text-neutral-500 mt-2">
                <span>
                  {signups?.series?.[0]?.date
                    ? new Date(signups.series[0].date).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                    : ""}
                </span>
                {peakDay && peakDay.count > 0 && (
                  <span className="text-neutral-400">
                    Peak: {peakDay.count} on {new Date(peakDay.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                )}
                <span>Today</span>
              </div>
              <div className="mt-4 pt-4 border-t border-white/[0.05] grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xl font-semibold text-white">{overview?.signups_24h ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">24h</div>
                </div>
                <div>
                  <div className="text-xl font-semibold text-white">{overview?.signups_7d ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">7d</div>
                </div>
                <div>
                  <div className="text-xl font-semibold text-white">{overview?.total_users ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">All-time</div>
                </div>
              </div>
            </div>

            {/* Plan distribution */}
            <div className="card-surface p-5 lg:col-span-2" data-testid="plan-dist-card">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-4">
                <Users size={13} /> Plan Distribution
              </div>
              <PlanDistBar plan_breakdown={overview?.plan_breakdown} total={overview?.active_paid || 0} />
              {overview?.active_paid > 0 && (
                <div className="mt-5 pt-4 border-t border-white/[0.05] text-xs text-neutral-500">
                  <b className="text-neutral-300">{overview.active_paid}</b> paying customer{overview.active_paid !== 1 ? "s" : ""}
                  {overview.mrr > 0 && (
                    <> · Avg: <b className="text-neutral-300">{fmtUSD(overview.mrr / overview.active_paid)}</b>/mo</>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Trial → Paid conversion funnel + nurture email performance */}
          <ConversionCard data={conversion} />

          {/* Paid-ad attribution: clicks → signups → cards on file → paid, per utm_campaign */}
          <AdFunnelCard />

          {/* Signed up but no card yet → hourly email/text nudge */}
          <CheckoutNudgeCard />

          {/* Is the Meta pixel really wired? + texts to the founder's phone */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PixelHealthCard />
            <OwnerAlertsCard />
          </div>

          {/* Monday email: est. spend vs signups vs paid → real cost per customer */}
          <WeeklyReportCard />

          {/* /start A/B tests: layout (mobile) + headline + plan anchor */}
          <ExperimentsCard />

          {/* drop-off text, trial-ending heads-up, day-one funnel email */}
          <FollowUpCard />

          {/* Activity: recent signups + recent paid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-surface p-5" data-testid="recent-signups-card">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-4">
                <Clock size={13} /> Latest Signups
              </div>
              {activity?.latest_signups?.length ? (
                <div>
                  {bulkSelectMode && (
                    <div className="flex items-center justify-between gap-3 pb-3 mb-2 border-b border-white/[0.05]" data-testid="bulk-action-bar">
                      <div className="text-[11px] text-neutral-400">
                        <b className="text-white">{selectedUserIds.size}</b> selected
                        {selectedUserIds.size > 0 && (
                          <button
                            onClick={() => setSelectedUserIds(new Set())}
                            className="ml-2 underline text-neutral-500 hover:text-white"
                            data-testid="bulk-clear-selection"
                          >
                            clear
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            const allIds = new Set(activity.latest_signups.map((u) => u.id));
                            setSelectedUserIds(allIds);
                          }}
                          data-testid="bulk-select-all"
                          className="text-[10px] px-2 py-1 rounded-md border border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
                        >
                          Select all
                        </button>
                        <button
                          onClick={() => bulkFlagAsTest(true)}
                          disabled={bulkBusy || selectedUserIds.size === 0}
                          data-testid="bulk-mark-test-btn"
                          className="text-[10px] px-3 py-1 rounded-md bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/25 disabled:opacity-40 font-semibold"
                        >
                          {bulkBusy ? "…" : "Flag as test"}
                        </button>
                        <button
                          onClick={() => bulkFlagAsTest(false)}
                          disabled={bulkBusy || selectedUserIds.size === 0}
                          data-testid="bulk-unflag-btn"
                          className="text-[10px] px-3 py-1 rounded-md bg-white/[0.03] border border-white/10 text-neutral-400 hover:text-white hover:border-white/25 disabled:opacity-40 font-semibold"
                        >
                          Un-flag
                        </button>
                      </div>
                    </div>
                  )}
                  {activity.latest_signups.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      testid={`latest-signup-${u.email}`}
                      onFlagChange={() => reload(range, hideTestUsers)}
                      bulkMode={bulkSelectMode}
                      isSelected={selectedUserIds.has(u.id)}
                      onToggleSelect={toggleUserSelection}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-neutral-500 text-sm py-6 text-center border border-dashed border-white/10 rounded-lg">
                  No signups yet.
                </div>
              )}
            </div>
            <div className="card-surface p-5" data-testid="recent-paid-card">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-4">
                <ArrowUpRight size={13} className="text-emerald-400" /> Latest Paid Conversions
              </div>
              {activity?.latest_paid?.length ? (
                <div>
                  {activity.latest_paid.map((u) => (
                    <UserRow key={u.id} u={u} testid={`latest-paid-${u.email}`} onFlagChange={() => reload(range, hideTestUsers)} />
                  ))}
                </div>
              ) : (
                <div className="text-neutral-500 text-sm py-6 text-center border border-dashed border-white/10 rounded-lg">
                  No paid conversions yet. Your first paying customer is coming. 💪
                </div>
              )}
            </div>
          </div>

          {/* Mercury Reaper ops-health widget */}
          <MercuryReaperCard />

          {/* Rate Card Share metric — proves shipper-facing distribution */}
          <RateCardShareCard />
        </>
      )}
    </div>
  );
}
